import React,{useEffect,useRef}from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import colors from "../../../../theme/colors";
import { borderRadiusPx } from "../../../../theme/borderRadius";

gsap.registerPlugin(ScrollTrigger);

const frameContext = require.context(
  "../../../../static/scroll-video",
  false,
  /\.webp$/
);

const frames = frameContext
  .keys()
  .sort()
  .map(frameContext);

const GlobalCommunityAnimation = ({ children }) => {
  const visualRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const visual = visualRef.current;
    const canvas = canvasRef.current;
    const section = visual?.parentElement;

    if (!section || !visual || !canvas || !frames.length) return;

    const context = canvas.getContext("2d");
    const images = [];
    const frameState = { index: 0 };
    let loaded = 0;

    const drawFrame = (index) => {
      const image = images[index];

      if (!image || !image.complete || !image.naturalWidth) return;

      const width = canvas.clientWidth;
      const height = canvas.clientHeight;

      if (!width || !height) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const targetWidth = Math.round(width * dpr);
      const targetHeight = Math.round(height * dpr);

      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
      }

      const imageRatio = image.naturalWidth / image.naturalHeight;
      const canvasRatio = width / height;

      let drawWidth;
      let drawHeight;
      let x;
      let y;

      if (imageRatio > canvasRatio) {
        drawHeight = height;
        drawWidth = height * imageRatio;
        const isMobile = window.innerWidth <= 600;
        x = isMobile ? (width - drawWidth) / 2 - (width * 0.3) : (width - drawWidth) / 2;
        y = 0;
      } else {
        drawWidth = width;
        drawHeight = width / imageRatio;
        x = 0;
        y = (height - drawHeight) / 2;
      }

      context.clearRect(0, 0, width, height);
      context.drawImage(image, x, y, drawWidth, drawHeight);
    };

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);

      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawFrame(frameState.index);
    };

    frames.forEach((src, index) => {
      const image = new Image();

      image.onload = () => {
        loaded += 1;

        if (index === 0) {
          resizeCanvas();
        }
      };

      image.src = src;
      images.push(image);
    });

    let ctx = gsap.context(() => {
      gsap.set(section,{
        backgroundColor: colors.neutral.white
      });

      gsap.set(visual,{
        width:"90%",
        height:"18vh",
        marginLeft:"auto",
        marginRight:"auto",
        marginTop:"82vh",
        borderRadius: borderRadiusPx.xxxl,
        backgroundColor: colors.home.navyDeep
      });

      const expandAnimation = gsap.timeline({
        paused:true,
        defaults:{ ease:"none" }
      });

      expandAnimation.to(visual,{
        width:"100%",
        height:"100vh",
        marginTop:"0",
        borderRadius:"0px",
        duration:1,
        onUpdate:() => {
          if (loaded) {
            drawFrame(Math.round(frameState.index));
          }
        }
      });

      ScrollTrigger.create({
        trigger:section,
        start:"top bottom",
        end:"top top",
        scrub:true,
        animation:expandAnimation,
        invalidateOnRefresh:true,
        onUpdate:(self) => {
          gsap.set(section,{
            backgroundColor:gsap.utils.interpolate(
              colors.neutral.white,
              colors.home.navyDeep,
              self.progress
            )
          });
        }
      });

      const videoAnimation = gsap.timeline({
        paused:true,
        defaults:{ ease:"none" }
      });

      videoAnimation.to(frameState,{
        index:frames.length - 1,
        duration:1,
        ease:"none",
        onUpdate:() => {
          if (!loaded) return;

          drawFrame(
            Math.round(frameState.index)
          );
        }
      });

      ScrollTrigger.create({
        trigger:section,
        start:"top top",
        end:"+=40%",
        scrub:true,
        pin:true,
        animation:videoAnimation,
        invalidateOnRefresh:true
      });
    }, section);

    const handleResize = () => {
      resizeCanvas();
      ScrollTrigger.refresh();
    };

    window.addEventListener("resize",handleResize);

    requestAnimationFrame(() => {
      resizeCanvas();
      ScrollTrigger.refresh();
    });

    return () => {
      ctx.revert();
      window.removeEventListener("resize",handleResize);
    };
  },[]);

  return (
    <div
      ref={visualRef}
      className="global-community-visual"
    >
      <canvas
        ref={canvasRef}
        className="global-community-canvas"
      />
      {children}
    </div>
  );
};

export default GlobalCommunityAnimation;